import { jsxs as _jsxs } from "react/jsx-runtime";
import { isValidElement, Fragment, useCallback, useContext, useEffect } from 'react';
import classNames from 'classnames';
import * as CustomRenderer from 'UIComponents/utils/propTypes/customRenderer';
import * as ScreenSizes from '../constants/ScreenSizes';
import { MediaQueryStylesContext } from '../context/MediaQueryStylesContext';
import { getMediaQueryClass, mediaQueryClassRegex } from './MediaQueryStyles';

const getRendererComputedClasses = (Renderer, ...args) => {
  if ( /*#__PURE__*/isValidElement(Renderer)) {
    args.unshift(Renderer.props.className);
  }

  return classNames(...args);
};

const renderChild = (child, props) => {
  return CustomRenderer.render(child, props, {
    mergeType: 'aggressive'
  });
};

export default function MediaQuery({
  alternate,
  children,
  minScreenSize,
  maxScreenSize,
  className = ''
}) {
  const {
    hasInjectedGlobalStyles
  } = useContext(MediaQueryStylesContext);
  const getPassedMediaQueryClasses = useCallback(() => {
    return classNames(...(className.match(mediaQueryClassRegex) || ""));
  }, [className]);
  const renderChildren = useCallback(() => {
    if (!children) {
      return null;
    }

    const mediaQueryClassConfig = {
      [getMediaQueryClass(ScreenSizes.XS, minScreenSize)]: !!minScreenSize,
      [getMediaQueryClass(maxScreenSize, 'min')]: !!maxScreenSize
    };
    const computedClasses = getRendererComputedClasses(children, getPassedMediaQueryClasses(), mediaQueryClassConfig);
    return renderChild(children, {
      className: computedClasses
    });
  }, [children, maxScreenSize, minScreenSize, getPassedMediaQueryClasses]);
  const renderAlternate = useCallback(() => {
    if (!alternate) {
      return null;
    }

    const mediaQueryClassConfig = {
      [getMediaQueryClass(minScreenSize, maxScreenSize)]: minScreenSize && maxScreenSize,
      [getMediaQueryClass(minScreenSize, 'min')]: minScreenSize && !maxScreenSize,
      [getMediaQueryClass(maxScreenSize, 'max')]: maxScreenSize && !minScreenSize
    };
    const computedClasses = getRendererComputedClasses(alternate, getPassedMediaQueryClasses(), mediaQueryClassConfig);
    return renderChild(alternate, {
      className: computedClasses
    });
  }, [alternate, maxScreenSize, minScreenSize, getPassedMediaQueryClasses]);
  useEffect(() => {
    if (!hasInjectedGlobalStyles) {
      console.warn('[MediaQuery] Global styles not injected.');
    }
  }, [hasInjectedGlobalStyles]);
  useEffect(() => {
    if (!(minScreenSize || maxScreenSize)) {
      console.warn('[MediaQuery] Either min/maxScreenSize required.');
    }
  }, [maxScreenSize, minScreenSize]);

  if (!hasInjectedGlobalStyles) {
    return children;
  }

  if (!(minScreenSize || maxScreenSize)) {
    return children;
  }

  return /*#__PURE__*/_jsxs(Fragment, {
    children: [renderChildren(), renderAlternate()]
  });
}